import { UseQueryResult } from '@tanstack/react-query';
import { ApiResponse } from 'apisauce';
import React from 'react';
import { Alert, Box, Typography } from '@mui/material';

import { ErrorBlock } from '../forms/ErrorBlock';
import { Spinner } from './Spinner';

export type QueryRendererActions = Pick<
    UseQueryResult,
    'refetch' | 'isRefetching' | 'isFetching'
>;

interface NoDataProps {
    noDataText?: string;
}

interface Props<T> {
    results: UseQueryResult<ApiResponse<T>>;
    children: (data: T, actions: QueryRendererActions) => React.ReactNode;
    NoDataComponent?: React.ComponentType<NoDataProps>;
}

export function QueryRenderer<T>({
    results,
    children,
    NoDataComponent = DefaultNoDataComponent,
}: Props<T>) {
    const { data, error, status, refetch, isRefetching, isFetching } = results;

    // Generated by Copilot - Memoize actions object to prevent re-renders
    const actions = React.useMemo(
        () => ({
            refetch,
            isRefetching,
            isFetching,
        }),
        [refetch, isRefetching, isFetching]
    );

    switch (status) {
        case 'error':
            return (
                <Box display="flex" marginTop={5} justifyContent="center">
                    <ErrorBlock error={error as Error}>
                        <Typography
                            variant="h6"
                            display="flex"
                            textAlign="center"
                            alignItems="center"
                        >
                            Oh No
                            <Typography
                                variant="body1"
                                textAlign="center"
                                marginLeft={1}
                            >
                                - Looks like we had an error loading!
                            </Typography>
                        </Typography>
                    </ErrorBlock>
                </Box>
            );
        case 'pending':
            return <LoadingSpinner />;
        case 'success':
            if (isRefetching) {
                return <LoadingSpinner />;
            }
            if (data.data) {
                return <>{children(data.data, actions)}</>;
            } else {
                return <NoDataComponent />;
            }
    }
}

export function DefaultNoDataComponent({
    noDataText = 'Looks like there is no data for this page!',
}: NoDataProps) {
    return (
        <Box display="flex" marginTop={5} justifyContent="center">
            <Alert severity="warning">
                <Typography
                    variant="h6"
                    display="flex"
                    textAlign="center"
                    alignItems="center"
                >
                    No Data
                    <Typography
                        variant="body1"
                        textAlign="center"
                        marginLeft={1}
                    >
                        {`- ${noDataText}`}
                    </Typography>
                </Typography>
            </Alert>
        </Box>
    );
}

function LoadingSpinner() {
    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            flex={1}
        >
            <Spinner />
        </Box>
    );
}
